import {
    getStorageItem,
    removeStorageItem,
    setStorageItem,
} from "~/lib/storage/Storage";
import { STORAGE_KEYS } from "~/types/Storage";

export const OESTRA_CAPTURE_INTENT = {
    ACCEPT: "accept",
    SKIP: "skip",
    EXIT_SAVE: "exit_save",
} as const;

export type OestraCaptureIntent =
    (typeof OESTRA_CAPTURE_INTENT)[keyof typeof OESTRA_CAPTURE_INTENT];

/** Safeguard only; PQP must still match `quiz_id` to the completed quiz. */
export const OESTRA_PQP_CAPTURE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export type OestraPqpCaptureState = {
    capture_intent: OestraCaptureIntent;
    quiz_id: string;
    created_at: number;
};

export const parseOestraPqpCapture = (): OestraPqpCaptureState | null => {
    const raw = getStorageItem(STORAGE_KEYS.OESTRA_PQP_CAPTURE);

    if (!raw) {
        return null;
    }

    try {
        const parsed = JSON.parse(raw) as Partial<OestraPqpCaptureState>;

        if (
            !parsed.capture_intent
            || !parsed.quiz_id
            || typeof parsed.created_at !== "number"
        ) {
            return null;
        }

        return {
            capture_intent: parsed.capture_intent,
            quiz_id: parsed.quiz_id,
            created_at: parsed.created_at,
        };
    } catch {
        return null;
    }
};

export const isOestraPqpCaptureValidForQuiz = (quizId: string) => {
    const state = parseOestraPqpCapture();

    if (!state) {
        return false;
    }

    if (state.quiz_id !== quizId) {
        return false;
    }

    if (Date.now() - state.created_at > OESTRA_PQP_CAPTURE_TTL_MS) {
        return false;
    }

    return true;
};

export const clearOestraPqpCapture = () => {
    removeStorageItem(STORAGE_KEYS.OESTRA_PQP_CAPTURE);
};

/** Drops stale capture from a prior incomplete run; keeps capture after quiz completion. */
export const clearOestraPqpCaptureIfQuizIncomplete = (
    quizCompletedStorageKey: string,
) => {
    if (getStorageItem(quizCompletedStorageKey) !== "true") {
        clearOestraPqpCapture();
    }
};

export const persistOestraPqpCapture = (
    state: Pick<OestraPqpCaptureState, "capture_intent" | "quiz_id">,
) => {
    const payload: OestraPqpCaptureState = {
        ...state,
        created_at: Date.now(),
    };

    setStorageItem(STORAGE_KEYS.OESTRA_PQP_CAPTURE, JSON.stringify(payload));
};
